import React, { useState, useEffect, useMemo, useCallback } from "react";
import { fmtDate, serverFetch, useGetIsDesktop } from "@extensions";
import { useUser } from "@redux";
import { useSelector } from "react-redux";
import { message, Spin, Card, Button, Space, Popconfirm, Table } from "antd";
import Payment from "../payment/payment";
import dayjs from "dayjs";
import { useFamilyContext, FamilyContextSelect, ForWhomCell } from "@components/family/family-context";
import "./sales.css";

const SalesUnpaid = ({ refreshKey = 0, onLoadingChange }) => {
  const user = useUser();
  const company = useSelector((s) => s.company);
  const isDesktop = useGetIsDesktop();
  const [sales, setSales] = useState([]);
  const [deadlineSecondsMap, setDeadlineSecondsMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  // Фильтр по группе: состав, выбранный вариант и отбор позиций
  const {
    familyMembers,
    selfClient,
    hasFamily,
    activeContext,
    setActiveContext,
    showForColumn,
    filterByContext,
  } = useFamilyContext(user);

  useEffect(() => {
    onLoadingChange?.(loading);
    return () => onLoadingChange?.(false);
  }, [loading, onLoadingChange]);

  const fetchSales = () => {
    if (!user) return;
    setLoading(true);
    serverFetch("OnlineClient/Sales", { }, user)
      .then((data) => {
        setSales(data || []);
        return data || [];
      })
      .catch((error) => {
        console.error("Ошибка получения продаж:", error);
        message.error(error?.UserMessage || "Не удалось загрузить список покупок");
        setSales([]);
        return [];
      })
      .then((sales) => {
        setLoading(false);
        // Получаем оставшиеся секунды до дедлайна с сервера (учитывает часовой пояс компании)
        const salesWithDeadline = (sales || []).filter((s) => s.paymentDeadline);
        if (salesWithDeadline.length > 0) {
          const idsParam = salesWithDeadline.map((s) => `ids=${s.id}`).join("&");
          serverFetch(`OnlineClient/SalesDeadlineSeconds?${idsParam}`, { method: "GET" }, user)
            .then((secondsMap) => {
              if (secondsMap && typeof secondsMap === "object") {
                setDeadlineSecondsMap(secondsMap);
              }
            })
            .catch((error) => {
              console.error("Ошибка получения секунд до дедлайна:", error);
            });
        }
      });
  };

  useEffect(() => {
    if (user) {
      fetchSales();
    }
  }, [user, refreshKey]);

  // Локальный обратный отсчёт минут (обновляется раз в минуту, показываем только минуты)
  useEffect(() => {
    const timer = setInterval(() => {
      setDeadlineSecondsMap((prev) => {
        const entries = Object.entries(prev);
        const hasPositive = entries.some(([, value]) => value > 0);
        if (!hasPositive) return prev;
        const next = {};
        entries.forEach(([key, value]) => {
          next[key] = value > 0 ? Math.max(0, value - 60) : value;
        });
        return next;
      });
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const filteredSales = filterByContext(sales);

  const totalAmount = filteredSales.reduce((sum, sale) => sum + (sale.syma || 0), 0);

  const formatDate = (dateStr) => {
    return fmtDate(dateStr) || "";
  };

  const formatDocDate = (dateStr) => {
    if (!dateStr) return "";
    const date = dayjs(dateStr);
    const today = dayjs();
    if (date.isSame(today, 'day')) {
      return date.format("HH:mm:ss");
    }
    return date.format("DD.MM.YYYY HH:mm:ss");
  };

  // Форматирует дедлайн оплаты с обратным отсчетом
  const formatDeadlineCountdown = (saleId, dateStr) => {
    if (!dateStr) return "";
    const seconds = deadlineSecondsMap[saleId];
    const diffMinutes = seconds != null ? Math.floor(seconds / 60) : 0;
    if (diffMinutes <= 0) {
      return `Оплатить до: ${formatDocDate(dateStr)} (срок истёк)`;
    }
    return `Оплатить до: ${formatDocDate(dateStr)} (осталось ${diffMinutes} мин...)`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    return dayjs(timeStr, "HH:mm:ss").format("HH:mm");
  };

  const formatPrice = (price) => {
    if (!price) return "0";
    return price.toLocaleString("ru-RU");
  };

  const formatRecord = (sale) => {
    if (!sale.recordDate) return "";
    const date = formatDate(sale.recordDate);
    const start = sale.recordStart ? formatTime(sale.recordStart) : "";
    const finish = sale.recordFinish ? formatTime(sale.recordFinish) : "";
    if (start && finish) {
      return `${date} ${start}-${finish}`;
    }
    return date;
  };

  const handleDelete = useCallback(async (saleId) => {
    try {
      await serverFetch(`OnlineClient/Sale/${saleId}`, { method: "DELETE" }, user);
      message.success("Продажа успешно удалена");
      fetchSales(); // Обновляем список продаж
    } catch (error) {
      console.error("Ошибка удаления продажи:", error);
      message.error(error?.userMessage || "Не удалось удалить продажу");
    }
  }, [user, fetchSales]);

  const columns = useMemo(() => [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Дата",
      dataIndex: "docDate",
      key: "docDate",
      width: 120,
      render: (date) => formatDate(date),
    },
    {
      title: "Наименование",
      dataIndex: "productName",
      key: "productName",
    },
    {
      title: "Кол.",
      dataIndex: "qty",
      key: "qty",
      width: 80,
      align: "right",
    },
    ...(showForColumn
      ? [
          {
            title: "Для кого",
            key: "for",
            width: 140,
            render: (_, record) => (
              <ForWhomCell clientId={record.clientId} familyMembers={familyMembers} user={selfClient} unknownInitial={selfClient?.name || "Себе"} />
            ),
          },
        ]
      : []),
    {
      title: "Сумма",
      dataIndex: "syma",
      key: "syma",
      width: 120,
      align: "right",
      render: (price) => `${formatPrice(price)} руб.`,
    },
    {
      title: "Сотрудник",
      dataIndex: "recordUserName",
      key: "recordUserName",
      width: 150,
    },
    {
      title: "Запись",
      key: "record",
      width: 200,
      render: (_, record) => formatRecord(record),
    },
    {
      title: "Оплатить до",
      dataIndex: "paymentDeadline",
      key: "paymentDeadline",
      width: 150,
      render: (date, record) => date ? formatDeadlineCountdown(record.id, date) : "",
    },
    {
      title: "Действия",
      key: "actions",
      width: 120,
      align: "center",
      render: (_, record) => (
        record.canBeDeletedByClient ? (
          <Popconfirm
            title="Удалить продажу?"
            description="Вы уверены, что хотите удалить эту продажу?"
            onConfirm={() => handleDelete(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button type="link" danger size="small">
              Удалить
            </Button>
          </Popconfirm>
        ) : null
      ),
    },
  ], [handleDelete, showForColumn, familyMembers, company, deadlineSecondsMap]);

  const handlePay = () => {
    setPaymentOpen(true);
  };

  return (
    <div className="sales-content">
      {hasFamily && (
        <FamilyContextSelect
          familyMembers={familyMembers}
          items={sales}
          activeContext={activeContext}
          onChange={setActiveContext}
          className="sales-context"
          labelClassName="sales-context-label"
          selectClassName="sales-context-select"
        />
      )}
      {loading ? (
        <div className="sales-loading">
          <Spin size="large" />
        </div>
      ) : isDesktop ? (
        <>
          {filteredSales.length > 0 ? (
            <Table
              columns={columns}
              dataSource={filteredSales}
              rowKey="id"
              pagination={false}
              className="sales-table"
            />
          ) : (
            <div className="sales-empty">Нет покупок</div>
          )}
          {filteredSales.length > 0 && (
            <div className="sales-total">
              <Space size="large" style={{ width: "100%", justifyContent: "space-between" }}>
                <div className="sales-total-text">К оплате: {formatPrice(totalAmount)} руб.</div>
                <Button type="primary" onClick={handlePay}>Оплатить</Button>
              </Space>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="sales-list">
            {filteredSales.length > 0 ? (
              filteredSales.map((sale) => (
                <Card key={sale.id} className="sales-card" size="small">
                  <div className="sales-card-content">
                    <div className="sales-card-header">
                      <div className="sales-card-date">{formatDate(sale.docDate)}</div>
                      <div className="sales-card-price">{formatPrice(sale.syma)} руб.</div>
                    </div>
                    {showForColumn && (
                      <ForWhomCell
                        clientId={sale.clientId}
                        familyMembers={familyMembers}
                        user={selfClient}
                        unknownInitial={selfClient?.name || "Себе"}
                        className="sales-card-for"
                      />
                    )}
                    {sale.deleted && <span className="sales-card-deleted">Удалено</span>}
                    <div className="sales-card-body">
                      <div className="sales-card-info">
                        {sale.productName && <div className="sales-card-product">{sale.productName}</div>}
                        {sale.qty && (
                          <div className="sales-card-qty">Количество: {sale.qty}</div>
                        )}
                        {sale.recordUserName && (
                          <div className="sales-card-employee">Сотрудник: {sale.recordUserName}</div>
                        )}
                        {sale.recordDate && (
                          <div className="sales-card-record">Запись: {formatRecord(sale)}</div>
                        )}
                        {sale.paymentDeadline && (
                          <div className="sales-card-deadline">{formatDeadlineCountdown(sale.id, sale.paymentDeadline)}</div>
                        )}
                      </div>
                      {sale.canBeDeletedByClient && (
                        <div className="sales-card-actions">
                          <Popconfirm
                            title="Удалить продажу?"
                            description="Вы уверены, что хотите удалить эту продажу?"
                            onConfirm={() => handleDelete(sale.id)}
                            okText="Да"
                            cancelText="Нет"
                          >
                            <Button type="link" danger size="small">
                              Удалить
                            </Button>
                          </Popconfirm>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))
            ) : (
              <div className="sales-empty">Нет покупок</div>
            )}
          </div>
          {filteredSales.length > 0 && (
            <div className="sales-total">
              <Space size="large" style={{ width: "100%", justifyContent: "space-between" }}>
                <div className="sales-total-text">К оплате: {formatPrice(totalAmount)} руб.</div>
                <Button type="primary" onClick={handlePay}>Оплатить</Button>
              </Space>
            </div>
          )}
        </>
      )}
      <Payment sales={filteredSales} open={paymentOpen} onClose={() => setPaymentOpen(false)}/>
    </div>
  );
};

export default SalesUnpaid;

